import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import axios from "axios";
import "../../../assets/css/OtherNewsSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    day: String(date.getDate()).padStart(2, "0"),
    month: date.toLocaleString("en", { month: "short" }).toUpperCase(),
    year: date.getFullYear(),
  };
}

function hasImage(value) {
  return (
    typeof value === "string" &&
    value.trim() !== "" &&
    value !== "undefined" &&
    value !== "null"
  );
}

function ArticleDate({ value, compact = false }) {
  const date = formatDate(value);
  if (!date) return null;
  return (
    <time
      className={`fmw-journal-date${compact ? " is-compact" : ""}`}
      dateTime={`${date.year}-${String(new Date(value).getMonth() + 1).padStart(2, "0")}-${date.day}`}
    >
      <span>{date.month}</span>
      <strong>{date.day}</strong>
      <span>{date.year}</span>
    </time>
  );
}

function ArticleImage({ item, className = "", eager = false }) {
  if (hasImage(item?.passport)) {
    return (
      <img
        className={className}
        src={`${BACKEND_URL}upload/${item.passport}`}
        alt={item.title || "FMW news article"}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
      />
    );
  }
  return (
    <div className={`${className} fmw-journal-placeholder`}>
      <i className="fa-regular fa-newspaper" aria-hidden="true" />
      <span>FMW JOURNAL</span>
    </div>
  );
}

function OtherNewsSection({ datacontent = [], initialPagination, id, type }) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [selectedStoryId, setSelectedStoryId] = useState(
    datacontent?.[0]?.id ?? null,
  );
  const [page, setPage] = useState(initialPagination?.current_page || 1);
  const [pagination, setPagination] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const headerTitle = datacontent?.[0]?.title || "News & Media";

  useEffect(() => {
    setNewsList(datacontent || []);
    setSelectedStoryId(datacontent?.[0]?.id ?? null);
    if (initialPagination) setPagination(initialPagination);
  }, [datacontent, initialPagination]);

  useEffect(() => {
    let active = true;
    async function fetchNews() {
      setLoading(true);
      setError(false);
      try {
        const endpoint = `${BASE_URL}posdata?pageName[eq]=${id}&columes[eq]=title_show_echo_03&ministry[eq]=0&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&display01[eq]=1&page=${page}&per_page=3`;

        const response = await axios.get(endpoint);
        if (!active) return;
        const items = response?.data?.allData || [];
        setNewsList(items);
        setSelectedStoryId(items?.[0]?.id ?? null);
        setPagination(
          response?.data?.pagination || { current_page: page, last_page: 1 },
        );
      } catch (err) {
        if (active) {
          setError(true);
          console.error("Unable to fetch FMW news:", err);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchNews();
    return () => {
      active = false;
    };
  }, [page, id, type, retryKey]);

  const currentPage = Number(pagination?.current_page || page);
  const lastPage = Math.max(1, Number(pagination?.last_page || 1));
  const goToPage = (nextPage) => {
    const target = Math.min(Math.max(nextPage, 1), lastPage);
    if (target !== page) setPage(target);
  };

  const featured = newsList[0];
  const selectedStory =
    newsList.find((item) => String(item.id) === String(selectedStoryId)) ||
    featured;
  const otherStories = newsList.filter(
    (item) => String(item.id) !== String(selectedStory?.id),
  );

  return (
    <section className="fmw-housing-journal" id="services">
      <div className="container fmw-journal-container">
        <header className="fmw-journal-heading">
          <div>
            <span className="fmw-journal-kicker">
              INFRASTRUCTURE · CONNECTIVITY · DEVELOPMENT
            </span>
            <h2>{headerTitle}</h2>
          </div>
          <p className="fmw-journal-section-label">
            <i className="fa fa-newspaper" aria-hidden="true"></i>
          </p>
        </header>

        {loading && (
          <div className="fmw-journal-status" role="status">
            <span className="fmw-journal-spinner" /> Updating the newsroom...
          </div>
        )}

        {error && newsList.length === 0 && (
          <div className="fmw-journal-empty" role="alert">
            <h3>We couldn't load the newsroom.</h3>
            <p>Please try again.</p>
            <button
              type="button"
              onClick={() => setRetryKey((value) => value + 1)}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && newsList.length === 0 && (
          <div className="fmw-journal-empty">
            <h3>No stories to display yet.</h3>
            <p>New ministry updates will appear here when available.</p>
          </div>
        )}

        {selectedStory && (
          <>
            <div className="fmw-journal-layout" aria-busy={loading}>
              <article className="fmw-journal-feature" aria-live="polite">
                <Link
                  to={`/read/${selectedStory.id}`}
                  className="fmw-journal-feature-image"
                  aria-label={`Read the full story: ${selectedStory.title || "news story"}`}
                >
                  <ArticleImage
                    item={selectedStory}
                    className="fmw-journal-feature-photo"
                    eager
                  />
                  <ArticleDate value={selectedStory.daterqw} />
                  <span className="fmw-journal-feature-image-label">
                    SELECTED STORY
                  </span>
                </Link>

                <div className="fmw-journal-feature-copy">
                  <span className="fmw-journal-story-meta">
                    <span className="fmw-journal-live-dot" /> MINISTRY DISPATCH
                  </span>
                  {selectedStory.caption && (
                    <div className="fmw-journal-caption">
                      <HTMLDisplay
                        html={selectedStory.caption}
                        textNo={15}
                        type="title"
                      />
                    </div>
                  )}
                  <h3>
                    <Link to={`/read/${selectedStory.id}`}>
                      <HTMLDisplay
                        html={selectedStory.story01}
                        textNo={30}
                        type="title"
                      />
                    </Link>
                  </h3>
                  <div className="fmw-journal-feature-summary">
                    <HTMLDisplay
                      html={selectedStory.story01}
                      textNo={headerTitle === "SPEECHES" ? 15 : 0}
                      type="body"
                    />
                  </div>
                  <Link
                    to={`/read/${selectedStory.id}`}
                    className="fmw-journal-feature-link"
                  >
                    Read the full story <span aria-hidden="true">↗</span>
                  </Link>
                </div>
              </article>

              <aside className="fmw-journal-rail">
                <div className="fmw-journal-rail-heading">
                  <div>
                    <span>FROM THE NEWSROOM</span>
                    <h3>More Stories</h3>
                  </div>
                  <span className="fmw-journal-rail-number">
                    {String(newsList.length).padStart(2, "0")}
                  </span>
                </div>

                {newsList.length <= 1 ? (
                  <div className="fmw-journal-single-note">
                    <p>
                      This is the only story currently available on this page.
                      Use the full story link to read more.
                    </p>
                  </div>
                ) : (
                  <div className="fmw-journal-story-list">
                    {newsList.map((item, index) => {
                      const isSelected =
                        String(item.id) === String(selectedStory?.id);
                      return (
                        <article
                          className={`fmw-journal-story ${isSelected ? "is-selected" : ""}`}
                          key={item.id || index}
                        >
                          <span className="fmw-journal-story-number">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <button
                            type="button"
                            className="fmw-journal-story-select"
                            onClick={() => setSelectedStoryId(item.id)}
                            aria-pressed={isSelected}
                            aria-label={`Show summary for ${item.title || "this story"}`}
                          >
                            <ArticleImage
                              item={item}
                              className="fmw-journal-story-photo"
                            />
                          </button>
                          <div className="fmw-journal-story-copy">
                            <ArticleDate value={item.daterqw} compact />
                            {item.caption && (
                              <span className="fmw-journal-story-caption">
                                <HTMLDisplay
                                  html={item.caption}
                                  textNo={7}
                                  type="title"
                                />
                              </span>
                            )}
                            <button
                              type="button"
                              className="fmw-journal-story-title-button"
                              onClick={() => setSelectedStoryId(item.id)}
                              aria-pressed={isSelected}
                            >
                              <HTMLDisplay
                                html={item.story01}
                                textNo={8}
                                type="title"
                              />
                            </button>
                            <button
                              type="button"
                              className="fmw-journal-story-read"
                              onClick={() => setSelectedStoryId(item.id)}
                              aria-pressed={isSelected}
                            >
                              {isSelected
                                ? "Currently selected"
                                : "Preview story"}{" "}
                              <span aria-hidden="true">→</span>
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}

                <div className="fmw-journal-archive">
                  <span className="fmw-journal-archive-mark">
                    <i className="fa-solid fa-book-open" aria-hidden="true" />
                  </span>
                  <div>
                    <span>THE ARCHIVE</span>
                    <p>Every update. One newsroom.</p>
                  </div>
                </div>
              </aside>
            </div>

            <nav
              className="fmw-journal-pagination"
              aria-label="News pagination"
            >
              <span className="fmw-journal-page-label">
                PAGE <strong>{currentPage}</strong> OF {lastPage}
              </span>
              <div className="fmw-journal-page-buttons">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1 || loading}
                  aria-label="Previous page"
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= lastPage || loading}
                  aria-label="Next page"
                >
                  Next <span aria-hidden="true">→</span>
                </button>
              </div>
            </nav>
          </>
        )}
      </div>
    </section>
  );
}

export default React.memo(OtherNewsSection);
